import { bind, classNames, Component, createRef, effect, h, signal, signalObject, tag } from "omi";

import { NWCloseButton } from "@/components/atoms/nw-button/closeButton.tsx";
import { ModalProps } from "@/components";
import { tailwind } from "@/tailwind";

export type ImageZoomModalData = {
  href: string;
  fit?: boolean;
};

type Props = ModalProps<ImageZoomModalData>;

@tag("nw-image-zoom-modal")
export class ImageZoomModal extends Component<Props> {
  static css = [tailwind];

  private isZoomed = signal(false);
  private isDragging = signal(false);
  private inDragMode = signal(false);
  private dragPos = signalObject({ x: 0, y: 0 });
  private scaleFactor = 2;
  private imgRef = createRef<HTMLImageElement>();
  private handleMouseMove?: (e: MouseEvent) => void;
  private handleMouseUp?: () => void;

  installed() {
    effect(() => {
      this.isZoomed.value ? this.attachDragListeners() : this.detachDragListeners();
    });
  }

  @bind
  private attachDragListeners() {
    this.imgRef.current?.addEventListener("dragstart", this.handleDragStart);
  }

  @bind
  private detachDragListeners() {
    const img = this.imgRef.current;
    if (img) {
      img.removeEventListener("dragstart", this.handleDragStart);
      document.removeEventListener("mousemove", this.handleMouseMove!);
      document.removeEventListener("mouseup", this.handleMouseUp!);
    }
  }

  @bind
  private handleDragStart(e: DragEvent) {
    e.preventDefault();
    this.inDragMode.value = true;
    this.isDragging.value = true;

    const initialX = this.dragPos.x.value;
    const initialY = this.dragPos.y.value;
    const startX = e.pageX - initialX;
    const startY = e.pageY - initialY;

    this.handleMouseMove = (e: MouseEvent) => {
      if (!this.inDragMode.value || this.imgRef.current == null) return;

      const x = e.pageX - startX;
      const y = e.pageY - startY;
      const imgRect = this.imgRef.current.getBoundingClientRect();
      const overflowX = imgRect.width > window.innerWidth;
      const overflowY = imgRect.height > window.innerHeight;

      this.dragPos.x.value = overflowX ? x : 0;
      this.dragPos.y.value = overflowY ? y : 0;

      this.imgRef.current.style.transitionProperty = "none";
      this.imgRef.current.style.transform = `translate(${this.dragPos.x.value}px, ${this.dragPos.y.value}px) scale(${this.scaleFactor})`;
    };

    this.handleMouseUp = () => {
      if (this.handleMouseMove) {
        document.removeEventListener("mousemove", this.handleMouseMove);
      }

      if (this.handleMouseUp) {
        document.removeEventListener("mouseup", this.handleMouseUp);
      }

      if (this.imgRef.current == null) return;

      const img = this.imgRef.current;
      const scaledWidth = img.offsetWidth * this.scaleFactor;
      const scaledHeight = img.offsetHeight * this.scaleFactor;
      const imgRect = img.getBoundingClientRect();
      const overflowX = imgRect.width > window.innerWidth;
      const overflowY = imgRect.height > window.innerHeight;
      const yDelta = scaledHeight - window.innerHeight;
      const xDelta = scaledWidth - window.innerWidth;

      this.dragPos.x.value = overflowX ? Math.min(Math.max(this.dragPos.x.value, xDelta * -1), xDelta) : 0;
      this.dragPos.y.value = overflowY ? Math.min(Math.max(this.dragPos.y.value, yDelta * -1), yDelta) : 0;

      img.style.transition = "transform 0.3s ease-out";
      img.style.transform = `translate(${this.dragPos.x.value}px, ${this.dragPos.y.value}px) scale(${this.scaleFactor})`;
      this.isDragging.value = false;

      setTimeout(() => {
        this.inDragMode.value = false;
      }, 100);
    };

    document.addEventListener("mousemove", this.handleMouseMove);
    document.addEventListener("mouseup", this.handleMouseUp);
  }

  @bind
  private resetDragPosition() {
    this.dragPos.x.value = 0;
    this.dragPos.y.value = 0;

    if (this.imgRef.current) {
      this.imgRef.current.style.transform = `translate(0px, 0px) scale(${this.scaleFactor})`;
    }
  }

  @bind
  private handleOnClick(e: MouseEvent) {
    e.stopPropagation();

    if (this.isDragging.value || this.inDragMode.value) return;

    if (this.isZoomed.value) this.resetDragPosition();
    this.isZoomed.value = !this.isZoomed.value;
  }

  render({ data, close }: Props) {
    return (
      <div class="w-screen h-screen flex items-center justify-center" onClick={close}>
        <div class="fixed bottom-4 sm:top-4 sm:right-4 animate-fadeIn duration-150 z-[100]">
          <NWCloseButton onClick={close} size="lg" theme="dark" />
        </div>

        <div
          className={classNames("max-w-screen-lg h-fit w-fit", {
            "max-h-[90vh] h-[90vh]": data.fit,
          })}
        >
          <img
            src={data.href}
            alt={`Zoomed in version of ${data.href}`}
            onClick={this.handleOnClick}
            ref={this.imgRef}
            class={classNames("transition-all", {
              "scale-[2] cursor-zoom-out": this.isZoomed.value,
              "active:cursor-grabbing": this.inDragMode.value,
              "cursor-zoom-in !transform-none": !this.isZoomed.value,
              "object-contain h-full": data.fit,
            })}
          />
        </div>
      </div>
    );
  }
}
